import type { ColumnDef, FilterFn } from '@tanstack/react-table';

import DeviceStatusBadge from '@/components/Devices/DeviceStatusBadge';
import type { IDeviceListItem } from '@/feature/device/types/device';
import {
  formatDeviceMode,
  formatDevicePlatform,
  isRentedStatus,
} from '@/feature/device/utils/deviceLabels';
import { formatDate } from '@/shared/utils/dateUtils';

import DeviceViewButton from './DeviceViewButton';
import type { DeviceAction } from './deviceActionTypes';

interface IDeviceColumnsOptions {
  showRentedTo?: boolean;
  onAction: (action: DeviceAction, device: IDeviceListItem) => void;
}

const noTruncateMeta = { noTruncate: true } as const;

export const deviceGlobalFilterFn: FilterFn<IDeviceListItem> = (row, _columnId, filterValue) => {
  const device = row.original;
  const search = String(filterValue ?? '')
    .trim()
    .toLowerCase();
  if (search === '') {
    return true;
  }
  return (
    device.device_name.toLowerCase().includes(search) ||
    device.device_id.toLowerCase().includes(search) ||
    formatDevicePlatform(device.platform).toLowerCase().includes(search)
  );
};

export function getDeviceColumns({
  showRentedTo = true,
  onAction,
}: IDeviceColumnsOptions): ColumnDef<IDeviceListItem>[] {
  const columns: ColumnDef<IDeviceListItem>[] = [
    {
      accessorKey: 'device_name',
      header: 'Device Name',
      meta: { ...noTruncateMeta, width: '180px' },
      cell: (info) => (
        <button
          type='button'
          className='text-theme-600 font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0 text-left whitespace-normal'
          onClick={() => onAction('view', info.row.original)}
        >
          {info.getValue() as string}
        </button>
      ),
    },
    {
      accessorKey: 'device_id',
      header: 'Device ID',
      meta: { ...noTruncateMeta, width: '110px' },
    },
    {
      accessorKey: 'platform',
      header: 'Platform',
      meta: { ...noTruncateMeta, width: '90px' },
      cell: (info) => formatDevicePlatform(info.getValue() as string),
    },
    {
      accessorKey: 'device_mode',
      header: 'Device Mode',
      meta: { ...noTruncateMeta, width: '120px' },
      cell: (info) => formatDeviceMode(info.getValue() as string),
    },
    {
      accessorKey: 'device_status',
      header: 'Status',
      meta: { ...noTruncateMeta, width: '120px' },
      cell: (info) => <DeviceStatusBadge status={info.getValue() as string} />,
    },
  ];

  if (showRentedTo) {
    columns.push({
      id: 'rented_to',
      header: 'Rented To',
      meta: { ...noTruncateMeta, width: '140px' },
      cell: (info) => info.row.original.rented_to?.org_name || '—',
    });
  }

  columns.push(
    {
      id: 'rental_start_at',
      header: 'Rental Start',
      meta: { ...noTruncateMeta, width: '110px' },
      cell: (info) =>
        isRentedStatus(info.row.original.device_status) ||
        info.row.original.device_status?.toLowerCase() === 'maintenance'
          ? formatDate(info.row.original.rental_start_at) || '—'
          : '—',
    },
    {
      id: 'rental_end_at',
      header: 'Rental End',
      meta: { ...noTruncateMeta, width: '110px' },
      cell: (info) =>
        isRentedStatus(info.row.original.device_status) ||
        info.row.original.device_status?.toLowerCase() === 'maintenance'
          ? formatDate(info.row.original.rental_end_at) || '—'
          : '—',
    },
    {
      id: 'actions',
      header: 'Action',
      enableSorting: false,
      meta: { width: '72px', align: 'center' as const },
      cell: (info) => <DeviceViewButton device={info.row.original} onAction={onAction} />,
    }
  );

  return columns;
}
